import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShareNetwork, Trash, MapPin, Clock, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BoardDetail() {
  const { boardId } = useParams();
  const [board, setBoard] = useState(null);

  const load = () => api.get(`/boards/${boardId}`).then((r) => setBoard(r.data)).catch(() => setBoard(false));
  useEffect(() => { load(); }, [boardId]);

  const share = () => {
    const url = `${window.location.origin}/shared/${board.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied", { description: url });
  };

  const removeListing = async (lid) => {
    await api.delete(`/boards/${boardId}/listings/${lid}`);
    toast.success("Removed from board");
    load();
  };

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/boards/${boardId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${board.name}-itinerary.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (board === false) return <div className="pt-32 text-center text-muted-foreground">Board not found.</div>;
  if (!board) return <div className="pt-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Link to="/boards" className="inline-flex items-center gap-1 text-sm font-medium text-[#63675E] hover:text-[#2E523A] mb-6 transition-colors duration-200" data-testid="back-to-boards">
          <ArrowLeft size={16} /> All boards
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold">{board.name}</h1>
            {board.description && <p className="text-[#63675E] mt-2 max-w-xl">{board.description}</p>}
            <p className="text-sm text-[#63675E] mt-2">{board.listings?.length || 0} destinations saved</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="rounded-full h-12 px-6 border-[#D2D5CD] hover:border-[#2E523A] hover:bg-[#EAEBE6] transition-colors duration-200" data-testid="download-pdf-btn">
              <FilePdf size={20} weight="duotone" className="text-[#2E523A]" /> {downloading ? "Preparing…" : "Download PDF"}
            </Button>
            <Button onClick={share} variant="outline" className="rounded-full h-12 px-6 border-[#2E523A] text-[#2E523A] hover:bg-[#2E523A] hover:text-white transition-colors duration-200" data-testid="share-board-btn">
              <ShareNetwork size={20} /> Share board
            </Button>
          </div>
        </div>

        {(!board.listings || board.listings.length === 0) ? (
          <div className="text-center py-20 border border-dashed border-[#D2D5CD] rounded-2xl" data-testid="empty-board-detail">
            <p className="text-[#63675E] mb-4">No destinations saved yet.</p>
            <Link to="/explore"><Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f]">Add destinations</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {board.listings.map((l, i) => (
              <div key={l.id} className="group relative overflow-hidden rounded-2xl border border-[#D2D5CD] bg-white animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`board-listing-${l.id}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={l.image} alt={l.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <button onClick={() => removeListing(l.id)} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-destructive hover:text-white transition-colors duration-200" data-testid={`remove-listing-${l.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold">{l.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-[#63675E] mt-1">
                    <span className="flex items-center gap-1"><MapPin size={14} /> {l.location}</span>
                    <span className="flex items-center gap-1"><Clock size={14} /> {l.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
